import type { FormEvent, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export type ContactFormValues = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type ContactFormProps = {
  formData: ContactFormValues;
  isSubmitting: boolean;
  onChange: (field: keyof ContactFormValues, value: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
};

export function ContactForm({
  formData,
  isSubmitting,
  onChange,
  onSubmit,
  onCancel,
}: ContactFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <Label htmlFor="contact-name" required>
            Full name
          </Label>
          <Input
            id="contact-name"
            name="name"
            autoComplete="name"
            required
            value={formData.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="Jane Doe"
          />
        </Field>
        <Field>
          <Label htmlFor="contact-email" required>
            Email address
          </Label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="jane@example.com"
          />
        </Field>
      </div>

      <Field>
        <Label htmlFor="contact-subject" required>
          Subject
        </Label>
        <Input
          id="contact-subject"
          name="subject"
          required
          value={formData.subject}
          onChange={(e) => onChange("subject", e.target.value)}
          placeholder="What is this regarding?"
        />
      </Field>

      <Field>
        <Label htmlFor="contact-message" required>
          Message
        </Label>
        <Textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          value={formData.message}
          onChange={(e) => onChange("message", e.target.value)}
          placeholder="Describe your issue or question in detail…"
          className="resize-y min-h-[140px]"
        />
      </Field>

      <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} className="gap-2">
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Send message
        </Button>
      </div>
    </form>
  );
}

function Field({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}
